import { useEffect, useState } from 'react';
import { apiBaseUrl } from '../authConfig.js';

export type GetToken = () => Promise<string | null>;

export interface ApiState<T> {
  readonly data: T | null;
  readonly error: string | null;
  readonly loading: boolean;
  /* Re-reads the endpoint. A screen that changes something server-side must be
     able to show the result of that change rather than its own optimistic
     guess at it. */
  readonly refresh: () => void;
}

/**
 * Every screen reads from the API the same way, so the failure modes are the
 * same everywhere: a network or auth failure surfaces as an error the screen
 * shows, never as an empty table that reads like "we looked and found nothing".
 */
export function useApi<T>(path: string, getToken: GetToken): ApiState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    void (async () => {
      try {
        const token = await getToken();
        const response = await fetch(`${apiBaseUrl}${path}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!response.ok) throw new Error(`API returned ${response.status}`);
        const body = (await response.json()) as T;
        if (!cancelled) setData(body);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Unknown error');
      }
    })();
    return () => { cancelled = true; };
  }, [path, getToken, nonce]);

  return {
    data, error, loading: data === null && error === null,
    refresh: () => { setNonce((n) => n + 1); },
  };
}
